import React, { useCallback, useState } from 'react';
import { Button, Flex, Text } from '@vibe/core';
import { useApprovalStore } from '../../stores/approvalStore';
import { approveTimesheet } from '../../api/timesheet-approval.api';
import { useTimesheetPermissions } from '../../hooks/useTimesheetPermissions';
import type { TimesheetSubmission } from '../../types/timesheet-approval.types';

export interface TimesheetApproveRejectBarProps {
  submission: TimesheetSubmission;
  currentUserId: string;
  isAdmin: boolean;
  onActionComplete: (updated: TimesheetSubmission) => void;
}

const TimesheetApproveRejectBar: React.FC<TimesheetApproveRejectBarProps> = ({
  submission,
  currentUserId,
  isAdmin,
  onActionComplete,
}) => {
  const setRejectDialogOpen = useApprovalStore((s) => s.setRejectDialogOpen);
  const setReopenDialogOpen = useApprovalStore((s) => s.setReopenDialogOpen);
  const setStatusHistoryDialogOpen = useApprovalStore(
    (s) => s.setStatusHistoryDialogOpen,
  );

  const { canApprove, canReject, canReopen } = useTimesheetPermissions(
    submission,
    currentUserId,
    isAdmin,
  );

  const [approving, setApproving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleApprove = useCallback(async () => {
    setApproving(true);
    setError(null);
    try {
      const result = await approveTimesheet(submission.id);
      onActionComplete(result);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setApproving(false);
    }
  }, [submission.id, onActionComplete]);

  return (
    <div className="rp-ts-action-bar">
      <Flex gap={Flex.gaps.SMALL} align={Flex.align.CENTER}>
        {canApprove && (
          <Button
            kind={Button.kinds.PRIMARY}
            size={Button.sizes.SMALL}
            onClick={handleApprove}
            loading={approving}
            color={Button.colors.POSITIVE}
          >
            Approve
          </Button>
        )}

        {canReject && (
          <Button
            kind={Button.kinds.PRIMARY}
            size={Button.sizes.SMALL}
            onClick={() => setRejectDialogOpen(true)}
            color={Button.colors.NEGATIVE}
          >
            Reject
          </Button>
        )}

        {canReopen && (
          <Button
            kind={Button.kinds.SECONDARY}
            size={Button.sizes.SMALL}
            onClick={() => setReopenDialogOpen(true)}
          >
            Reopen
          </Button>
        )}

        <Button
          kind={Button.kinds.TERTIARY}
          size={Button.sizes.SMALL}
          onClick={() => setStatusHistoryDialogOpen(true)}
        >
          History
        </Button>
      </Flex>

      {error && (
        <Text type={Text.types.TEXT2} style={{ color: '#e2445c', marginTop: 4 }}>
          {error}
        </Text>
      )}
    </div>
  );
};

export default TimesheetApproveRejectBar;
